import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { finalize, firstValueFrom, map, Observable, Subject, takeUntil } from 'rxjs';
import { GetTransactionDetailRequestParams, TransactionDetailData } from './transactionDetails.interface';
import { Router } from '@angular/router';
import { TransactionDetailsService } from '../../services/transactionDetails/transactionDetails.service';
import { getOffset, getUtcDateRangeForLocalDate, getYesterday, showNotification } from 'src/app/shared/helpers/helpers';
import { ModalContent } from '../../shared/modal-content';
import { ModalComponent } from 'angular-custom-modal';
import { OptionItem } from '../../shared/types/api-responses.type';
import { MerchantsService } from '../../services/merchants/merchants.service';
import { ProvidersService } from '../../services/providers/providers.service';
import { CategoriesService } from '../../services/categories/categories.service';
import { FlatpickrOptions } from 'ng2-flatpickr';
import { UserData } from '../../interfaces/users.interface';
import { selectCurrentUser } from '../../services/users/user.selectors';
import { Store } from '@ngrx/store';
import { showAlert } from '../../shared/helpers/alert';
import { ProductsService } from '../../services/products/products.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-transactionDetails',
  templateUrl: './transactionDetails.component.html',
  styleUrls: ['./transactionDetails.component.css'],
})
export class TransactionDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('modal') modal!: ModalComponent;

  private destroy$ = new Subject<void>();
  loading = false;
  showAdvancedFilters = false; // Toggle for advanced filters
  merchants: OptionItem[] = [];
  providers: OptionItem[] = [];
  categories: OptionItem[] = [];
  products: OptionItem[] = [];

  @ViewChild('createdFromPicker') createdFromPicker: any;
  createdFromConfig: FlatpickrOptions = {
    altInput: true,
    altFormat: 'd-m-Y',
    dateFormat: 'Y-m-d',
  };

  @ViewChild('createdToPicker') createdToPicker: any;
  createdToConfig: FlatpickrOptions = {
    altInput: true,
    altFormat: 'd-m-Y', // ✅ Ditampilkan sebagai DD-MM-YYYY
    dateFormat: 'Y-m-d', // ✅ Disimpan dalam format backend-friendly
    minDate: '', // will be set dynamically
    defaultDate: new Date(),
  };
  // createdFrom = new Date();

  status: OptionItem[] = [
    { label: 'Inquiry', value: '3' },
    { label: 'Pending', value: '1' },
    { label: 'Failed', value: '2' },
    { label: 'Success', value: '0' },
    { label: 'Reversal', value: '4' },
  ];

  types: OptionItem[] = [
    { label: 'Transaction', value: '1' },
    { label: 'Deposit', value: '2' },
  ];

  modalData: ModalContent = new ModalContent({
    header: 'Confirmation',
    body: 'Are you sure you want to delete this transaction?',
    showCloseButton: true,
    showConfirmButton: true,
    closeButtonLabel: 'Cancel',
    confirmButtonLabel: 'Delete',
    closeButtonClass: 'btn-secondary',
    confirmButtonClass: 'btn-danger',
  });

  selectedId = '';

  params = new GetTransactionDetailRequestParams();
  totalRows = 0;

  cols = [
    { field: 'no', title: 'No' },

    // --- Field dari Struct TransactionDetail ---
    { field: 'id', title: 'ID' },
    { field: 'tx_id', title: 'Tx ID' },
    { field: 'bill_id', title: 'Bill ID' },
    { field: 'customer_address', title: 'Customer Address' },
    { field: 'product_name', title: 'Product Name' },
    { field: 'customer_name', title: 'Customer Name' },
    { field: 'period', title: 'Period' },
    { field: 'type', title: 'Type' },
    { field: 'stand_meter', title: 'Stand Meter' },
    { field: 'period_detail', title: 'Period Detail' },
    { field: 'bill_amount', title: 'Bill Amount' },
    { field: 'fine', title: 'Fine' },
    { field: 'admin_merchant', title: 'Admin Merchant' },
    { field: 'admin_provider', title: 'Admin Provider' },
    { field: 'total', title: 'Total' },
    { field: 'first_stand', title: 'First Stand' },
    { field: 'last_stand', title: 'Last Stand' },
    { field: 'ref_provider', title: 'Ref Provider' },
    { field: 'additional_info', title: 'Additional Info' },
    { field: 'additional_info_2', title: 'Additional Info 2' },
    { field: 'info', title: 'Info' },
    { field: 'created_at', title: 'Created At' },
    { field: 'updated_at', title: 'Updated At' },
  ];

  pageSizeOptions = [5, 10, 25, 50, 100];
  rows: TransactionDetailData[] = [];

  userAuth$!: Observable<UserData | null>;
  isAdmin$!: Observable<boolean>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private transactionDetailsService: TransactionDetailsService,
    private readonly merchantService: MerchantsService,
    private readonly providerService: ProvidersService, // private readonly roleService: RolesService,
    private readonly categoryService: CategoriesService,
    private readonly productsService: ProductsService,

    public storeData: Store<any>
  ) {}

  ngOnInit(): void {
    this.userAuth$ = this.storeData.select(selectCurrentUser);
    this.isAdmin$ = this.userAuth$.pipe(map((user) => user?.roles?.some((role) => role.code === 'admin') ?? false));

    this.getCategoryData().then();

    this.getProductData().then();

    const yesterday = getYesterday();

    // const { startUtc, endUtc } = getUtcDateRangeForLocalDate(yesterday);
    this.params.createdFrom = yesterday;
    this.params.createdTo = yesterday + ' 23:59:59';

    this.createdFromConfig = {
      altInput: true,
      altFormat: 'd-m-Y',
      dateFormat: 'Y-m-d',
      defaultDate: yesterday,
    };

    this.createdToConfig = {
      altInput: true,
      altFormat: 'd-m-Y',
      dateFormat: 'Y-m-d',
      defaultDate: yesterday,
    };

    this.userAuth$.subscribe((user) => {
      if (user) {
        this.isAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isAdmin) => {
          if (isAdmin) {
            this.getMerchantData().then();
            this.getProviderData().then();
          } else {
            this.params.merchantCode = user.merchant.code;
            this.getCategoryData().then();
          }
          this.getTransactionDetails().then();
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async getTransactionDetails(): Promise<void> {
    this.loading = true;
    this.rows = [];

    // get list for page
    try {
      this.transactionDetailsService
        .getTransactionDetailService(this.params)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.loading = false))
        )
        .subscribe({
          next: (response) => {
            this.rows = this.processDataForTable(response.data);
            this.totalRows = response.totalPage;
          },
          error: (err) => {
            console.error('Error fetching transactionDetails data:', err);
            showNotification('error', 'Failed to fetch transaction details', JSON.stringify(err));
          },
        });
    } catch (error) {
      showNotification('error', 'Failed to fetch transaction details', JSON.stringify(error));
      this.loading = false;
    }
  }

  private async getMerchantData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.merchantService.getMerchants());
      this.merchants = data.map((merchant) => ({ label: merchant.name, value: merchant.code }));
    } catch (error) {
      showNotification('error', 'Failed to fetch merchants', JSON.stringify(error));
    }
  }

  private async getProviderData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.providerService.getProviders());
      this.providers = data.map((provider) => ({ label: provider.name, value: provider.code }));
      console.log('providerOption: ', this.providers);
    } catch (error) {
      showNotification('error', 'Failed to fetch provider', JSON.stringify(error));
    }
  }

  private async getCategoryData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.categoryService.getCategories());
      this.categories = data.map((category) => ({ label: category.name, value: category.code }));
      console.log('categoryOption: ', this.categories);
    } catch (error) {
      showNotification('error', 'Failed to fetch category', JSON.stringify(error));
    }
  }

  processDataForTable(data: TransactionDetailData[]) {
    return data.map((each, i) => {
      const date: any = new Date(each.updated_at);

      const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

      const formattedDate = `${localDate.getFullYear()}-${(localDate.getMonth() + 1).toString().padStart(2, '0')}-${localDate
        .getDate()
        .toString()
        .padStart(2, '0')} ${localDate.getHours().toString().padStart(2, '0')}:${localDate.getMinutes().toString().padStart(2, '0')}:${localDate
        .getSeconds()
        .toString()
        .padStart(2, '0')}`;

      return {
        ...each,
        no: getOffset(this.params.pageNo, this.params.rowPerPage) + i + 1,
        updated_at: formattedDate,
      };
    });
  }

  changeServer(data: any): void {
    this.params.pageNo = data.current_page;
    this.params.rowPerPage = data.pagesize;
    this.params.filter = data.search;
    this.getTransactionDetails();
  }

  onInputChanged(value: string): void {
    console.log('Debounced Input:', value);
    this.params.filter = '';
    if (value) {
      this.params.filter = value;
    }
    this.cdRef.detectChanges();
  }

  goToForm(id: string): void {
    if (id === 'new') {
      this.router.navigate(['/transactionDetails/new']).then();
      return;
    }
    this.router.navigate(['/transactionDetails/edit', id]).then();
    return;
  }

  async deleteTransaction(id: string): Promise<void> {
    this.loading = true;

    try {
      const delResp = await firstValueFrom(this.transactionDetailsService.deleteTransactionDetailById(id));
      showNotification('success', 'Success to delete transaction Detail', JSON.stringify(delResp));
      this.getTransactionDetails();
    } catch (error) {
      console.error('Delete Transaction Error:', error);
    } finally {
      this.loading = false;
    }
  }

  async onModalDeleteConfirm(id: string): Promise<void> {
    await this.deleteTransaction(id);
    this.modal.close();
  }

  onMerchantChange(merchantOption: OptionItem): void {
    this.params.merchantCode = null;
    if (merchantOption) {
      console.log('Merchant Change:', merchantOption.value);
      console.log('Merchant Change:', merchantOption.label);
      this.params.merchantCode = merchantOption.value;
    }
    this.getTransactionDetails();
  }

  onProviderChange(providerOption: OptionItem): void {
    this.params.providerCode = null;
    if (providerOption) {
      console.log('Provider Change:', providerOption.value);
      console.log('Provider Change:', providerOption.label);
      this.params.providerCode = providerOption.value;
    }
    this.getTransactionDetails();
  }

  onCategoryChange(categoryOption: OptionItem): void {
    this.params.categoryCode = null;
    if (categoryOption) {
      console.log('Category Change:', categoryOption.value);
      console.log('Category Change:', categoryOption.label);
      this.params.categoryCode = categoryOption.value;
    }
    this.getTransactionDetails();
  }

  onStatusChange(statusOption: OptionItem): void {
    this.params.status = null;
    if (statusOption) {
      console.log('status Change:', statusOption.value);
      console.log('status Change:', statusOption.label);
      this.params.status = statusOption.value;
    }
    this.getTransactionDetails();
  }

  onCreatedFromChange(event: any): void {
    this.params.createdFrom = null;

    const selectedCreatedFromDate = event?.target ? event.target.value : event;
    console.log('Selected Created From date:', selectedCreatedFromDate);

    if (!selectedCreatedFromDate) return;

    const selectedDate = new Date(selectedCreatedFromDate);
    const formattedDate = selectedDate.toISOString().split('T')[0];
    console.log('Formatted Created From Date:', formattedDate);

    // Calculate minDate (1 month before) and maxDate (1 month after)
    const minDate = new Date(selectedDate);
    minDate.setMonth(minDate.getMonth() - 1);

    const maxDate = new Date(selectedDate);
    maxDate.setMonth(maxDate.getMonth() + 1);

    // Set min and max for the createdToPicker
    this.createdToPicker.flatpickr.set('minDate', minDate);
    this.createdToPicker.flatpickr.set('maxDate', maxDate);
    this.createdToPicker.flatpickr.set('disable', [(date: Date) => date < minDate || date > maxDate]);

    console.log('Allowed createdTo range:', minDate, 'to', maxDate);
    this.params.createdFrom = selectedCreatedFromDate;

    // Call your logic
    this.getTransactionDetails();
  }

  onCreatedToChange(event: any): void {
    this.params.createdTo = null;
    const selectedCreatedToDate = event?.target ? event.target.value : event;

    console.log('Selected Created To date:', selectedCreatedToDate);

    if (!selectedCreatedToDate) return;

    this.params.createdTo = selectedCreatedToDate + ' 23:59:59';
    console.log('Selected this.params:', this.params);

    // Call your function to get transactionDetails or any other logic you need
    this.getTransactionDetails();
  }

  get isCompactFilter(): boolean {
    return this.providers.length === 0 && this.merchants.length === 0;
  }

  private async getProductData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.productsService.getProducts());
      this.products = data.map((product) => ({ label: product.name, value: product.code }));
      console.log('productOption: ', this.products);
    } catch (error) {
      this.showNotification('error', 'Failed to fetch products', JSON.stringify(error));
    }
  }

  onProductChange(productOption: OptionItem): void {
    this.params.productCode = null;
    if (productOption) {
      console.log('Product Change:', productOption.value);
      console.log('Product Change:', productOption.label);
      this.params.productCode = productOption.value;
    }
    this.getTransactionDetails();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  async downloadExcel(): Promise<void> {
    this.loading = true;
    try {
      const params: GetTransactionDetailRequestParams = { ...this.params };
      params.rowPerPage = 1000000;
      params.pageNo = 1;

      const response = await firstValueFrom(this.transactionDetailsService.getTransactionDetailService(params));

      const dataForExcel = response.data.map((row: any, index: number) => ({
        'No': index + 1,
        'Tx ID': row.tx_id || '',
        'Bill ID': row.bill_id || '',
        'Customer Address': row.customer_address || '',
        'Product Name': row.product_name || '',
        'Customer Name': row.customer_name || '',
        'Period': row.period || '',
        'Type': row.type || '',
        'Stand Meter': row.stand_meter || '',
        'Period Detail': row.period_detail || '',
        'Bill Amount': row.bill_amount || 0,
        'Fine': row.fine || 0,
        'Admin Merchant': row.admin_merchant || 0,
        'Admin Provider': row.admin_provider || 0,
        'Total': row.total || 0,
        'First Stand': row.first_stand || 0,
        'Last Stand': row.last_stand || 0,
        'Ref Provider': row.ref_provider || '',
        'Additional Info': row.additional_info || '',
        'Additional Info 2': row.additional_info_2 || '',
        'Info': row.info || '',
        'Updated At': row.updated_at || ''
      }));

      // Generate filename: transaction_details_{date}.xlsx
      const date = this.params.createdFrom
        ? this.params.createdFrom.split(' ')[0]
        : new Date().toISOString().split('T')[0];
      const fileName = `transaction_details_${date}.xlsx`;

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataForExcel);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, fileName);

      showNotification('success', 'Excel downloaded successfully');
    } catch (error) {
      showNotification('error', 'Failed to download excel', JSON.stringify(error));
    } finally {
      this.loading = false;
    }
  }

  private showNotification(type: 'success' | 'error', title: string, text = 'Success'): void {
    showAlert({ type, title, text }).then();
  }
}
